import React from "react";

interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  borderType?: "blue" | "green" | "orange" | "red" | "purple" | "yellow";
  onClick?: () => void;
  icon?: React.ReactNode;
  /** Extra control beside the title, such as an info tooltip. */
  info?: React.ReactNode;
  /** Optional classes — use `h-full` in equal-height grids. */
  className?: string;
}

const colorClasses = {
  blue: {
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    subtitle: "text-blue-600",
    border: "border border-blue-100",
  },
  green: {
    iconBg: "bg-teal-100",
    iconColor: "text-teal-600",
    subtitle: "text-teal-600",
    border: "border border-teal-100",
  },
  orange: {
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
    subtitle: "text-orange-600",
    border: "border border-orange-100",
  },
  red: {
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    subtitle: "text-red-600",
    border: "border border-red-100",
  },
  purple: {
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    subtitle: "text-purple-600",
    border: "border border-purple-100",
  },
  yellow: {
    iconBg: "bg-yellow-100",
    iconColor: "text-yellow-600",
    subtitle: "text-yellow-600",
    border: "border border-yellow-100",
  },
};

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  borderType = "blue",
  onClick,
  icon,
  info,
  className = "",
}) => {
  const colors = colorClasses[borderType];

  return (
    <div
      className={`metric-card flex h-full flex-col bg-white rounded-lg p-4 ${
        colors.border
      } ${
        onClick
          ? "cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02]"
          : ""
      } ${className}`.trim()}
      onClick={onClick}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex items-start gap-3">
          {icon && (
            <div
              className={`${colors.iconBg} ${colors.iconColor} p-2 rounded-lg`}
            >
              <div className="w-5 h-5">{icon}</div>
            </div>
          )}
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-600">
              <span>{title}</span>
              {info}
            </div>
            {subtitle && (
              <div className="text-xs leading-snug text-gray-500">
                {subtitle}
              </div>
            )}
          </div>
        </div>
        <div
          className={
            typeof value === "string" && value === "Coming Soon"
              ? "mt-auto text-2xl font-bold text-gray-800"
              : "mt-auto text-2xl font-bold text-gray-900"
          }
        >
          {value}
        </div>
      </div>
    </div>
  );
};

interface MetricCardsProps {
  metrics: Array<{
    title: string;
    value: number | string;
    subtitle?: string;
    borderType?: "blue" | "green" | "orange" | "red" | "purple" | "yellow";
    onClick?: () => void;
    icon?: React.ReactNode;
  }>;
  className?: string;
}

const MetricCards: React.FC<MetricCardsProps> = ({ metrics }) => {
  return (
    <>
      {metrics.map((metric, index) => (
        <MetricCard
          key={index}
          title={metric.title}
          value={metric.value}
          subtitle={metric.subtitle}
          borderType={metric.borderType}
          onClick={metric.onClick}
          icon={metric.icon}
        />
      ))}
    </>
  );
};

export default MetricCards;
